import {
  Dialog,
  Hotkey,
  IconButton,
  SearchField,
  useInputSystem,
  useRegisterAction,
} from '@wandb/components';
import React, {useMemo, useState} from 'react';

import {INPUT_ACTION} from '../../inputSystem';
import {TOP_NAV_HEIGHT} from '../../util/constants';
import {useRampFlagShortcutDrawer} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {useKeyboardShortcutsDrawerStore} from './keyboardShortcutsDrawerStore';

/**
 * Turn a scope ID into a human-readable header label, since scopes carry no
 * separate display-name field. Scope IDs are camelCase object keys on the
 * input system definition (e.g. `mediaBrowser` -> `Media browser`).
 */
function humanizeScope(scope: string): string {
  const spaced = scope.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Flag gate. Kept as its own component so that when the flag is off we mount
 * nothing — in particular `useRegisterAction` below never runs, leaving Cmd+/
 * unbound. (An early `return null` after the hook would be an illegal
 * conditional hook call, so the gate has to be a component boundary.)
 */
export const KeyboardShortcutsDrawer = () => {
  const shortcutDrawerEnabled = useRampFlagShortcutDrawer();
  return shortcutDrawerEnabled ? <KeyboardShortcutsDrawerController /> : null;
};

/**
 * Owns the open/close state and keeps the Cmd+/ binding alive whenever the flag
 * is on. The `Dialog.Drawer` shell stays mounted so react-aria can play its
 * enter/exit animation off `isOpen`; the expensive content (which subscribes to
 * `useInputSystem`) lives in a child react-aria mounts only while the drawer is
 * open, so the input-system subscription doesn't churn on every scope push/pop.
 */
const KeyboardShortcutsDrawerController = () => {
  const isOpen = useKeyboardShortcutsDrawerStore(state => state.isOpen);
  const close = useKeyboardShortcutsDrawerStore(state => state.close);
  const toggle = useKeyboardShortcutsDrawerStore(state => state.toggle);

  // Bind the live handler for the Primary+Slash shortcut at runtime — the
  // static stub in inputSystem.ts has no access to this store.
  useRegisterAction(INPUT_ACTION.VIEW_KEYBOARD_SHORTCUTS, toggle);

  return (
    <Dialog.Drawer
      isOpen={isOpen}
      onOpenChange={isNowOpen => {
        if (!isNowOpen) {
          close();
        }
      }}
      placement="right"
      offsetTop={TOP_NAV_HEIGHT}>
      <KeyboardShortcutsDrawerContent />
    </Dialog.Drawer>
  );
};

/**
 * The drawer body. Mounted by react-aria only while the drawer is open (and
 * unmounted once the close animation finishes), so the `query` search state
 * resets on every reopen rather than persisting across open/close.
 */
const KeyboardShortcutsDrawerContent = () => {
  const close = useKeyboardShortcutsDrawerStore(state => state.close);
  const {bindings} = useInputSystem();
  const [query, setQuery] = useState('');

  // Global scope first; remaining scopes in their natural order. Skip scopes
  // with no bindings (structurally permitted but never arises organically).
  // `global` is a reserved scope in the inputSystem. When a search query is
  // present, filter each scope's bindings by name and drop empty groups.
  const visibleGroups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return Object.keys(bindings)
      .filter(scope => bindings[scope].length > 0)
      .sort((a, b) => {
        if (a === 'global') {
          return -1;
        }
        if (b === 'global') {
          return 1;
        }
        return 0;
      })
      .map(scope => ({
        scope,
        matches: normalizedQuery
          ? bindings[scope].filter(binding =>
              binding.name.toLowerCase().includes(normalizedQuery)
            )
          : bindings[scope],
      }))
      .filter(group => group.matches.length > 0);
  }, [bindings, query]);

  return (
    <>
      <div style={headerStyle}>
        <Dialog.Heading mb={0}>Keyboard shortcuts</Dialog.Heading>
        <IconButton
          icon="close"
          variant="ghost"
          aria-label="Close keyboard shortcuts"
          onPress={close}
        />
      </div>
      <Dialog.Content>
        <div style={drawerContentStyle}>
          <SearchField
            aria-label="Search keyboard shortcuts"
            placeholder="Search shortcuts"
            fullWidth={true}
            value={query}
            onChange={setQuery}
          />
          {visibleGroups.map(({scope, matches}, index) => {
            const headingId = `kbd-shortcuts-scope-${scope}`;
            return (
              <React.Fragment key={scope}>
                {index > 0 && (
                  <hr className="mt-24 border-0 border-t border-solid border-moon-250" />
                )}
                <section aria-labelledby={headingId} style={scopeGroupStyle}>
                  <h3 id={headingId}>
                    {humanizeScope(scope === 'global' ? 'essentials' : scope)}
                  </h3>
                  <ul style={shortcutListStyle}>
                    {matches.map(binding => (
                      <li key={binding.id} style={shortcutRowStyle}>
                        <span>{binding.name}</span>
                        <Hotkey keys={binding.shortcut} name={binding.name} />
                      </li>
                    ))}
                  </ul>
                </section>
              </React.Fragment>
            );
          })}
          {visibleGroups.length === 0 && (
            <p style={emptyStateStyle}>No matching shortcuts</p>
          )}
        </div>
      </Dialog.Content>
    </>
  );
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: 32,
};

const scopeGroupStyle: React.CSSProperties = {
  marginTop: 24,
};

const shortcutListStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
};

const shortcutRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '8px 0',
};

const drawerContentStyle: React.CSSProperties = {
  padding: '0 32px 32px 32px',
};

const emptyStateStyle: React.CSSProperties = {
  margin: '24px 0 0 0',
  textAlign: 'center',
};
